import JSZip from "jszip"

function saveAs(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
}

async function getImageData(url: string) {
    try {
        const response = await fetch(url)
        if (!response.ok) {
            console.error(`Failed to fetch image: ${url}`, response.status)
            return
        }
        const blob = await response.blob()
        return new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.readAsDataURL(blob)
        })
    } catch (error) {
        console.error(`Error fetching image: ${url}`, error)
        return
    }
}

function dataURLtoBlob(dataurl) {
    let arr = dataurl.split(","), mime = arr[0].match(/:(.*?);/)[1],
       bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n)
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
    }
    return new Blob([u8arr], {type: mime})
}

function useZipper({files}: {
    files: { originalName: string; name: string; content: string | Blob }[]
}) {
    const zip = new JSZip()
    files.forEach((file) => {
        if(!file) return
        zip.file(file.name, file.content)
    })
    return zip.generateAsync({type: "blob"})
}

function flattenPath(path: string) {
    let cleanPath = path.split("?")[0]
    try {
        if (cleanPath.startsWith("http")) {
            cleanPath = new URL(cleanPath).pathname
        }
    } catch {
        // fall through to basename of the raw string
    }
    return cleanPath.split("/").pop() || "image"
}

export async function usePackageDownloader({email, group, builder}) {
    const name = `${email.alias}-${group.alias}`
    console.log("usePackageDownloader", {email, group, builder})
    usePackageMaker({email, group, builder}).then((blob) => {
        if (!blob) return
        saveAs(blob, `${name}.zip`)
    })
}

export async function usePackageMaker({email, group, builder}) {
    if (!email) {
        console.error("Email not found")
        return
    }

    console.log("usePackageMaker", {email, group, builder})

    // Extract base image paths without timestamps
    // Make sure builder.images exists before trying to use it
    if (!builder || !builder.images) {
        console.error("Builder or builder.images is undefined", builder);
        return null;
    }
    
    const imagePaths = Array.from(builder.images.values())
    const cleanImagePaths = imagePaths.map(path => {
        // Remove timestamp query parameters if present
        return (path as string).split('?')[0]
    })

    const usedNames = new Set<string>()
    const uniqueName = (base: string) => {
        if (!usedNames.has(base)) {
            usedNames.add(base)
            return base
        }
        const dotIdx = base.lastIndexOf(".")
        const stem = dotIdx > -1 ? base.slice(0, dotIdx) : base
        const ext = dotIdx > -1 ? base.slice(dotIdx) : ""
        let i = 1
        let candidate = `${stem}-${i}${ext}`
        while (usedNames.has(candidate)) {
            i++
            candidate = `${stem}-${i}${ext}`
        }
        usedNames.add(candidate)
        return candidate
    }

    const images = await Promise.all(
       cleanImagePaths.map(async (imagePath) => {
           // get image data from url
           const imageUrl = imagePath as string

           if (!imageUrl) {
               console.error("Image URL not found", imagePath)
               return
           }

           // Cache-bust only relative URLs; CDN URLs are fetched as-is to
           // avoid breaking signed URLs or polluting cache keys.
           const fetchUrl = imageUrl.startsWith("http")
               ? imageUrl
               : `${imageUrl}?t=${Date.now()}`
           const imageDataUrl = await getImageData(fetchUrl)
           if (!imageDataUrl) return
           const imageData = dataURLtoBlob(imageDataUrl)

           return {
               originalName: imagePath,
               name: uniqueName(flattenPath(imagePath)),
               content: imageData,
           } as {
               originalName: string;
               name: string;
               content: string | Blob
           }
       }))

    let fixedHTML: string = builder.html

    // Inline <style>/class CSS so the exported HTML survives email clients (Gmail/Outlook).
    // No-op when the email only uses inline styles.
    try {
        fixedHTML = await useCssInline().inlineCss(fixedHTML)
    } catch (e) {
        console.error("CSS inline (export) failed:", e)
    }

    // also flatten the image paths
    images.forEach((image) => {
        if (!image?.originalName) return
        console.log("Replacing", image, image.originalName, image.name)
        // Remove any timestamp parameters when replacing in HTML
        const originalPathWithoutParams = (image.originalName as string).split('?')[0]
        // Use regex to replace all instances of the path (with or without query params)
        const escapedPath = originalPathWithoutParams.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const replaceRegex = new RegExp(escapedPath + '(\\?[^"\'\\s]+)?', 'g')
        fixedHTML = fixedHTML.replace(replaceRegex, image.name)
    })


    const name = `${group?.alias}-${email?.alias}`
    return useZipper({
        files: [
            {
                originalName: "index.html",
                name: "index.html",
                content: fixedHTML
            },
            ...images
        ],
    })
}
